import React, { useEffect } from 'react';
import { Button } from '../Button/Button';
import type { ParameterChange } from './ParameterEditor.types';
import { getDisplayValue } from './ParameterEditor.utils';

interface ParameterEditorConfirmPanelProps {
  changes: ParameterChange[];
  onConfirm: () => void;
  onCancel: () => void;
  onUndoChange: (parameterId: string) => void;
  saving: boolean;
  confirmButtonText?: string;
  cancelButtonText?: string;
}

const ChangeRow: React.FC<{
  change: ParameterChange;
  onUndoChange: (id: string) => void;
  XIcon: React.ReactNode;
}> = ({ change, onUndoChange, XIcon }) => {
  const oldDisplay = getDisplayValue(
    change.oldValue,
    change.dataType,
    change.isSensitive,
    change.listOptions,
    change.oldEntityDisplayLabel
  );
  const newDisplay = getDisplayValue(
    change.newValue,
    change.dataType,
    change.isSensitive,
    change.listOptions,
    change.newEntityDisplayLabel
  );

  return (
    <div className="flex items-start justify-between gap-3 p-3 rounded-lg bg-bg-secondary dark:bg-dark-bg-secondary">
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-content-primary dark:text-dark-content-primary">
          {change.parameterName}
        </p>
        <div className="flex items-center gap-2 mt-1 text-sm">
          <span className="text-content-secondary dark:text-dark-content-secondary line-through truncate">
            {oldDisplay || '(vacío)'}
          </span>
          <span className="text-content-tertiary dark:text-dark-content-tertiary">
            →
          </span>
          <span className="text-content-primary dark:text-dark-content-primary font-medium truncate">
            {newDisplay || '(vacío)'}
          </span>
        </div>
      </div>
      <button
        type="button"
        onClick={() => onUndoChange(change.parameterId)}
        aria-label={`Quitar cambio en ${change.parameterName}`}
        className="
          shrink-0 flex items-center gap-1 px-2 py-1 rounded-md
          text-xs font-medium
          text-content-extensions-red dark:text-red-400
          hover:bg-red-50 dark:hover:bg-red-950/30
          transition-colors cursor-pointer
        "
      >
        {XIcon}
        Quitar
      </button>
    </div>
  );
};

export const ParameterEditorConfirmPanel: React.FC<ParameterEditorConfirmPanelProps> = ({
  changes,
  onConfirm,
  onCancel,
  onUndoChange,
  saving,
  confirmButtonText = 'Confirmar',
  cancelButtonText = 'Cancelar',
}) => {
  // Group changes by category, then by section
  const grouped = changes.reduce<
    Record<string, { direct: ParameterChange[]; sections: Record<string, ParameterChange[]> }>
  >((acc, change) => {
    if (!acc[change.categoryName]) {
      acc[change.categoryName] = { direct: [], sections: {} };
    }
    if (change.sectionName) {
      if (!acc[change.categoryName].sections[change.sectionName]) {
        acc[change.categoryName].sections[change.sectionName] = [];
      }
      acc[change.categoryName].sections[change.sectionName].push(change);
    } else {
      acc[change.categoryName].direct.push(change);
    }
    return acc;
  }, {});

  // Close on Escape key
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !saving) onCancel();
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [onCancel, saving]);

  const XIcon = (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 20 20"
      fill="currentColor"
      className="w-3.5 h-3.5"
    >
      <path d="M6.28 5.22a.75.75 0 0 0-1.06 1.06L8.94 10l-3.72 3.72a.75.75 0 1 0 1.06 1.06L10 11.06l3.72 3.72a.75.75 0 1 0 1.06-1.06L11.06 10l3.72-3.72a.75.75 0 0 0-1.06-1.06L10 8.94 6.28 5.22Z" />
    </svg>
  );

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50"
      onClick={(e) => {
        if (e.target === e.currentTarget) onCancel();
      }}
    >
      <div className="bg-bg-primary dark:bg-dark-bg-primary rounded-2xl shadow-xl max-w-[640px] w-full mx-4 max-h-[80vh] flex flex-col">
        {/* Header */}
        <div className="px-6 py-4 border-b border-border-primary dark:border-dark-border-primary">
          <h2 className="text-lg font-semibold text-content-primary dark:text-dark-content-primary">
            Resumen de cambios
          </h2>
          <p className="text-sm text-content-secondary dark:text-dark-content-secondary mt-1">
            {changes.length} {changes.length === 1 ? 'cambio pendiente' : 'cambios pendientes'}
          </p>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-6 py-4">
          {Object.entries(grouped).map(([categoryName, { direct, sections }]) => (
            <div key={categoryName} className="mb-6 last:mb-0">
              <h3 className="text-sm font-medium text-content-tertiary dark:text-dark-content-tertiary uppercase tracking-wider mb-3">
                {categoryName}
              </h3>

              {direct.length > 0 && (
                <div className="space-y-3">
                  {direct.map((change) => (
                    <ChangeRow key={change.parameterId} change={change} onUndoChange={onUndoChange} XIcon={XIcon} />
                  ))}
                </div>
              )}

              {Object.entries(sections).map(([sectionName, sectionChanges]) => (
                <div key={sectionName} className={direct.length > 0 ? 'mt-4' : ''}>
                  <h4 className="text-xs font-medium text-content-tertiary dark:text-dark-content-tertiary tracking-wider mb-2 ml-1">
                    {sectionName}
                  </h4>
                  <div className="space-y-3">
                    {sectionChanges.map((change) => (
                      <ChangeRow key={change.parameterId} change={change} onUndoChange={onUndoChange} XIcon={XIcon} />
                    ))}
                  </div>
                </div>
              ))}
            </div>
          ))}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-border-primary dark:border-dark-border-primary flex items-center justify-end gap-3">
          <Button
            type="outline"
            onClick={onCancel}
            disabled={saving}
          >
            {cancelButtonText}
          </Button>
          <Button
            onClick={onConfirm}
            disabled={saving || changes.length === 0}
          >
            {saving ? 'Guardando...' : confirmButtonText}
          </Button>
        </div>
      </div>
    </div>
  );
};
